import React, { useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, 
  Mail, 
  MessageSquare, 
  Send, 
  HelpCircle,
  Phone,
  Building
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportModal = ({ isOpen, onClose, jobId = null }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('options'); // 'options', 'email', 'message'
  const [loading, setLoading] = useState(false);
  const [messageData, setMessageData] = useState({
    subject: '',
    message: ''
  });

  const handleClose = () => {
    setActiveTab('options');
    setMessageData({ subject: '', message: '' });
    onClose();
  };

  const handleEmailSupport = () => {
    const subject = jobId ? `Support Request - Job ${jobId}` : 'Support Request';
    const body = `Hello JOP Support Team,

I need assistance with ${jobId ? `Job ID: ${jobId}` : 'my account'}.

Business: ${user.business_name}
Email: ${user.email}
Phone: ${user.phone}

Please describe your issue below:
`;
    
    const mailtoLink = `mailto:support@jop.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
    handleClose();
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageData.subject.trim() || !messageData.message.trim()) {
      toast.error('Please fill in both subject and message');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/support/message`, {
        subject: messageData.subject,
        message: messageData.message,
        job_id: jobId
      });

      toast.success('Your message has been sent to JOP Support. We\'ll get back to you shortly.');
      handleClose();
    } catch (error) {
      console.error('Failed to send support message:', error);
      const message = error.response?.data?.detail || 'Failed to send message';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-auto">
        
        {/* Options View */}
        {activeTab === 'options' && (
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <HelpCircle className="h-6 w-6 mr-2 text-blue-600" />
                  Contact Support
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Get help from the JOP Support team
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* User Info Display */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Your Information</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <div className="flex items-center">
                  <Building className="h-4 w-4 mr-2" />
                  {user.business_name}
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  {user.email}
                </div>
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  {user.phone}
                </div>
                {jobId && (
                  <div className="text-xs text-blue-600 mt-2">
                    Regarding Job: {jobId}
                  </div>
                )}
              </div>
            </div>

            {/* Support Options */}
            <div className="space-y-3">
              <button
                onClick={handleEmailSupport}
                className="w-full flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <Mail className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h4 className="font-medium text-gray-900">Email Support</h4>
                  <p className="text-sm text-gray-600">Open your email client with pre-filled details</p>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('message')}
                className="w-full flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <MessageSquare className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <h4 className="font-medium text-gray-900">Send Message</h4>
                  <p className="text-sm text-gray-600">Send a message directly through the platform</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Message Form View */}
        {activeTab === 'message' && (
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Send Message</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Send a message to JOP Support
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleMessageSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={messageData.subject}
                  onChange={handleInputChange}
                  className="jop-input"
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={messageData.message}
                  onChange={handleInputChange}
                  rows="5"
                  className="jop-input resize-none"
                  placeholder="Please describe your issue in detail..."
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="jop-button flex-1 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('options')}
                  className="jop-button-secondary"
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportModal;